<script setup lang="ts">
import { computed } from 'vue'
import { Button } from '@/components/ui/button'
import Loader from '@/shared/components/Loader.vue'
import Icon from '@/shared/components/Icon.vue'

import type { ButtonVariants } from '@/components/ui/button'
import type { Component } from 'vue'

type Severity = 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'warning' | 'help' | 'danger' | 'contrast'

type Props = {
  disabled?: boolean
  severity?: Severity
  variant?: 'outlined' | 'text' | 'link'
  size?: 'small' | 'large'
  icon?: Component
  type?: 'button' | 'submit' | 'reset'
  label?: string
  loading?: boolean
  loadingIcon?: Component
  dashed?: boolean

  // PrimeVue-style boolean style modifiers, kept for consumer compatibility
  text?: boolean
  outlined?: boolean
  link?: boolean
  fluid?: boolean

  // Deprecated
  square?: boolean
}

const props = defineProps<Props>()

const severityVariant: Partial<Record<Severity, ButtonVariants['variant']>> = {
  primary: 'default',
  secondary: 'secondary',
  danger: 'destructive',
  success: 'success',
  info: 'info',
  warn: 'warning',
  warning: 'warning',
  help: 'secondary',
  contrast: 'secondary'
}

const isOutlined = computed(() => props.dashed || props.outlined || props.variant === 'outlined')
const isText = computed(() => props.text || props.variant === 'text')
const isLink = computed(() => props.link || props.variant === 'link')

const buttonVariant = computed<ButtonVariants['variant']>(() => {
  if (isLink.value) return 'link'
  if (isOutlined.value) return 'outline'
  if (isText.value) return 'ghost'
  return (props.severity && severityVariant[props.severity]) || 'default'
})

const buttonSize = computed<ButtonVariants['size']>(() => {
  if (props.square) {
    return props.size === 'small' ? 'icon-sm' : props.size === 'large' ? 'icon-lg' : 'icon'
  }
  return props.size === 'small' ? 'sm' : props.size === 'large' ? 'lg' : 'default'
})
</script>

<template>
  <Button
    :variant="buttonVariant"
    :size="buttonSize"
    :disabled="props.disabled || props.loading"
    :type="props.type"
    :class="[props.dashed && 'dashed-button', props.fluid && 'w-full']"
    :style="props.disabled ? { cursor: 'not-allowed' } : undefined"
  >
    <Loader
      v-if="props.loading && !props.loadingIcon"
      size="small"
    />
    <Icon
      v-else-if="props.loading && props.loadingIcon"
      :icon="props.loadingIcon"
      severity="inherit"
    />
    <Icon
      v-else-if="props.icon"
      :icon="props.icon"
      severity="inherit"
    />
    <slot>{{ props.label }}</slot>
  </Button>
</template>

<style scoped>
.dashed-button {
  border-style: dashed;
}
</style>
